import * as CL from '@design-system/component-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { paths } from '../../common/constants/pathVariables.js';
import { useHideGlobalFooter } from '../../common/hooks/useHideGlobalFooter';
import { useNavigateBack } from '../../common/hooks/useNavigateBack.js';
import { customerMsg, processingMsg, productsMsg, somethingWentWrongMsg, t } from '../../common/i18n';
import { BillingAccountType } from '../../common/utils/billingAccountUtilsCoCo.js';
import { scrollTo } from '../../common/utils/browserUtils.js';
import { Order } from '../../generated/api/order.js';
import { getCheckedAddonKey } from '../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { LoadingOverlayCoCo } from './LoadingOverlayCoCo.js';
import { CheckoutContextCoCo } from './context/CheckoutContextCoCo.js';
import { useCheckoutOrchestratorCoCo } from './hooks/useCheckoutOrchestratorCoCo.js';
import { useInstallationSelectionInvalidationCoCo } from './hooks/useInstallationSelectionInvalidationCoCo.js';
import { usePlaceOrderCoCo } from './hooks/usePlaceOrderCoCo.js';
import './productConfig/productConfigSetupCoCo.js';
import { PlaceOrderStep, STEP_ORDER } from './placeOrderTypes.js';
import { CustomerStepCoCo } from './steps/CustomerStepCoCo.js';
import { ProductsStepCoCo } from './steps/ProductsStepCoCo.js';
import { PlaceOrderSummaryCoCo } from './steps/common/PlaceOrderSummaryCoCo.js';
import { DeliveryStepCoCo } from './steps/delivery/DeliveryStepCoCo.js';
import { ConfirmModal } from './steps/payment/ConfirmModal.js';
import { PaymentStepCoCo } from './steps/payment/PaymentStepCoCo.js';
import { isCardPaymentPossible, isDeviceItemsOnly } from './utils/cardPaymentUtilsCoCo.js';
import { updateContacts } from './utils/contactOrPurposeUtils.js';
import { getRelatedPartyForCustomerStep } from './utils/placeOrderUtilsCoCo.js';
import type { PlaceOrderStepState } from './placeOrderTypes.js';
import type {
  CheckoutStepValidationHandleCoCo,
  PlaceOrderFormDataCoCo,
  ShoppingBasketUpdateMethodsCoCo,
} from './placeOrderTypesCoCo.js';
import type { ShoppingBasketType } from '../../common/types/shoppingBasketCoCo.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { Contact } from '../../generated/api/contact.js';
import type { CustomerResponse } from '../../generated/api/customerResponse.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { AuthenticatedUser } from '../../public/site/authState.js';

import './PlaceOrderCoCo.scss';

export type { CustomerPrimaryAddress } from './placeOrderTypes.js';

const EMPLOYEE_STEP_ORDER: readonly PlaceOrderStep[] = STEP_ORDER.filter(step => step !== PlaceOrderStep.CUSTOMER);

const buildDefaultFormValues = (basket?: ShoppingBasketType): PlaceOrderFormDataCoCo => {
  // Track position within each product+addon group so cartItemInstanceId
  // ("productId-N") stays unique when the same product appears multiple times.
  const groupIndexMap = new Map<string, number>();
  const placeOrderItemUnits = (basket?.productOfferings ?? []).map(basketItem => {
    const groupKey = `${basketItem.id}|${getCheckedAddonKey(basketItem)}`;
    const groupIdx = groupIndexMap.get(groupKey) ?? 0;
    groupIndexMap.set(groupKey, groupIdx + 1);
    return {
      basketItemId: basketItem.cartItemId,
      basketItemIndex: groupIdx,
      productOfferAddons: [
        ...(basketItem.bundledAddonProductOffering ?? []),
        ...(basketItem.commercialAddonProductOffering ?? []),
      ],
      addons: [],
      installationContact: undefined,
    };
  });
  return {
    products: { placeOrderItemUnits },
    delivery: {},
    payment: { paymentType: Order.PaymentTypeEnum.BILLING_ACCOUNT },
  };
};

export interface PlaceOrderCoCoProps {
  shoppingBasket: ShoppingBasketType;
  user: AuthenticatedUser;
  mdmId: string;
  customerInfo: CustomerResponse;
  shoppingBasketUpdateMethods: ShoppingBasketUpdateMethodsCoCo;
  selectedProductOffers: ProductOfferingsResponse[];
  initialContacts: Contact[];
  billChannels?: BillChannel[];
  /** Employee (EOE) checkout skips the customer step */
  isEmployeeOrder?: boolean;
}

export const PlaceOrderCoCo = (props: PlaceOrderCoCoProps) => {
  const {
    mdmId,
    user,
    shoppingBasket,
    initialContacts,
    selectedProductOffers,
    customerInfo,
    shoppingBasketUpdateMethods,
    billChannels,
    isEmployeeOrder = false,
  } = props;
  const stepOrder: readonly PlaceOrderStep[] = isEmployeeOrder ? EMPLOYEE_STEP_ORDER : STEP_ORDER;
  const basketPath = isEmployeeOrder ? paths.EMPLOYEE_SHOPPING_CART : paths.SHOPPING_BASKET;
  const [contacts, setContacts] = useState<Contact[]>(initialContacts ?? []);
  const [activeStep, setActiveStep] = useState<PlaceOrderStep>(stepOrder[0]);
  const [stepState, setStepState] = useState<PlaceOrderStepState>({ completed: new Set() });
  const [pendingStep, setPendingStep] = useState<PlaceOrderStep | undefined>(undefined);
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);
  const [isStepLoading, setIsStepLoading] = useState(false);
  const [errorNotification, setErrorNotification] = useState<string>();
  const productsStepRef = useRef<CheckoutStepValidationHandleCoCo | null>(null);
  const deliveryStepRef = useRef<CheckoutStepValidationHandleCoCo | null>(null);
  const validationInProgress = useRef(false);
  const activeStepRef = useRef(activeStep);
  const stepActivationResolver = useRef<{ step: PlaceOrderStep; resolve: () => void } | null>(null);
  const contactMasterId = user.contact?.contactMasterId;
  const contactInfo = getRelatedPartyForCustomerStep(customerInfo.relatedParty, contactMasterId);
  const companyName = customerInfo.name;
  const navigateBack = useNavigateBack(basketPath, -1);

  const methods = useForm<PlaceOrderFormDataCoCo>({
    defaultValues: buildDefaultFormValues(shoppingBasket),
    mode: 'onChange',
  });
  const { control, getValues, reset, setValue } = methods;

  // Register the field array so react-hook-form tracks it properly for reset() and form state sync
  useFieldArray({ control, name: 'products.placeOrderItemUnits' });

  const {
    orderId,
    deliveryMethodGroups,
    deliveryOptions,
    customerPrimaryAddress,
    availableInstallationDates,
    installationAvailability,
    availabilityCheckedDate,
    checkInstallationAvailability,
    confirmInstallationTimeSlots,
    invalidateInstallationAvailability,
    stepHandlers,
  } = useCheckoutOrchestratorCoCo({
    basket: shoppingBasket,
    mdmId,
    user,
    contacts,
    selectedProductOffers,
    getFormValues: getValues,
    isEmployeeOrder,
    onDraftOrderError: () => setErrorNotification(t.JDTO(somethingWentWrongMsg)),
  });

  // Sync form state when basket changes (cross-tab localStorage sync).
  // Merges user-edited values with new structure by matching basketItemId + basketItemIndex.
  useEffect(() => {
    const currentValues = getValues();
    const mergedOrderItemUnits = buildDefaultFormValues(shoppingBasket).products.placeOrderItemUnits.map(
      newItem =>
        currentValues.products?.placeOrderItemUnits?.find(
          existing =>
            existing.basketItemId === newItem.basketItemId && existing.basketItemIndex === newItem.basketItemIndex
        ) || newItem
    );
    reset(
      { ...currentValues, products: { ...currentValues.products, placeOrderItemUnits: mergedOrderItemUnits } },
      { keepDefaultValues: true }
    );
  }, [shoppingBasket, getValues, reset]);

  useInstallationSelectionInvalidationCoCo({
    basket: shoppingBasket,
    control,
    getValues,
    setValue,
    invalidateInstallationAvailability,
    currentAvailabilityResults: installationAvailability.results,
  });

  useHideGlobalFooter();

  useEffect(() => {
    activeStepRef.current = activeStep;
    if (stepActivationResolver.current?.step === activeStep) {
      stepActivationResolver.current.resolve();
      stepActivationResolver.current = null;
    }
  }, [activeStep]);

  const refreshContacts = useCallback(async () => {
    try {
      setContacts((await updateContacts())?.contacts ?? []);
    } catch {
      // Silently continue — form still has the correct contactId even if refresh fails
    }
  }, []);

  const markCurrentStepCompleted = () => {
    setStepState(prev => ({ ...prev, completed: new Set([...prev.completed, activeStep]) }));
  };

  const handleStepAction = async (action: () => Promise<void>): Promise<boolean> => {
    try {
      await action();
      setErrorNotification(undefined);
      return true;
    } catch {
      setErrorNotification(t.JDTO(somethingWentWrongMsg));
      return false;
    }
  };

  const handleNextStep = async () => {
    setErrorNotification(undefined);
    setIsStepLoading(true);
    try {
      const stepHandler = stepHandlers[activeStep];
      if (stepHandler) {
        const ok = await handleStepAction(stepHandler);
        if (!ok) {
          return;
        }
      }
      markCurrentStepCompleted();
      const nextStep = stepOrder[stepOrder.indexOf(activeStep) + 1];
      if (nextStep) {
        setActiveStep(nextStep);
        scrollTo(0);
      }
    } finally {
      setIsStepLoading(false);
    }
  };

  const handlePreviousStep = () => {
    setErrorNotification(undefined);
    const currentIndex = stepOrder.indexOf(activeStep);
    if (currentIndex === 0) {
      navigateBack();
      return;
    }
    const previousStep = stepOrder[currentIndex - 1];
    if (previousStep) {
      setActiveStep(previousStep);
      scrollTo(0);
    }
  };

  const moveToStep = (targetStep: PlaceOrderStep) => {
    if (targetStep === activeStep) {
      return;
    }
    if (isCreatingNewBillingAccount && activeStep === PlaceOrderStep.PAYMENT) {
      setPendingStep(targetStep);
      return;
    }
    const currentIndex = stepOrder.indexOf(activeStep);
    const targetIndex = stepOrder.indexOf(targetStep);
    if (targetIndex < currentIndex) {
      setErrorNotification(undefined);
      setActiveStep(targetStep);
      scrollTo(0);
    }
  };

  const handleConfirmNavigation = () => {
    setIsCreatingNewBillingAccount(false);
    if (pendingStep) {
      setActiveStep(pendingStep);
      setPendingStep(undefined);
      scrollTo(0);
    }
  };

  const handleCancelNavigation = () => {
    setPendingStep(undefined);
  };

  const { isSubmittingOrder, submitOrder: onSubmit } = usePlaceOrderCoCo({
    orderId: orderId ?? '',
    getFormValues: methods.getValues,
    onError: () => setErrorNotification(t.JDTO(somethingWentWrongMsg)),
    isEmployeeOrder,
  });

  const activateStep = async (step: PlaceOrderStep): Promise<void> => {
    if (step === activeStepRef.current) {
      return;
    }
    return new Promise(resolve => {
      stepActivationResolver.current = { step, resolve };
      activeStepRef.current = step;
      setActiveStep(step);
      scrollTo(0);
    });
  };

  const validateBeforeSubmit = async (billingAccountId: string): Promise<void> => {
    if (validationInProgress.current || isSubmittingOrder) {
      return;
    }

    validationInProgress.current = true;
    setErrorNotification(undefined);
    setIsStepLoading(true);
    try {
      await activateStep(PlaceOrderStep.PRODUCTS);
      if (!(await productsStepRef.current?.validate())) {
        return;
      }

      await activateStep(PlaceOrderStep.DELIVERY);
      if (!(await deliveryStepRef.current?.validate())) {
        return;
      }

      await activateStep(PlaceOrderStep.PAYMENT);
      await onSubmit(billingAccountId);
    } finally {
      validationInProgress.current = false;
      setIsStepLoading(false);
    }
  };

  const contactsUpdateMethods = { contacts, onContactCreated: refreshContacts };
  const activeStepIndex = stepOrder.indexOf(activeStep);
  const isStepDisabled = (step: PlaceOrderStep) => stepOrder.indexOf(step) > activeStepIndex;
  const summaryProps = {
    shoppingBasket,
    selectedProductOffers,
    customerInfo,
    user,
    mdmId,
    shoppingBasketUpdateMethods,
    initialContacts: contacts,
  };

  const checkoutContextValue = useMemo(
    () => ({
      basket: shoppingBasket,
      selectedProductOffers,
      contacts,
      refreshContacts,
      mdmId,
      user,
      customerInfo,
      shoppingBasketUpdateMethods,
      orderId,
      setOrderId: () => {},
      isEmployeeOrder,
    }),
    [
      shoppingBasket,
      selectedProductOffers,
      contacts,
      refreshContacts,
      mdmId,
      user,
      customerInfo,
      shoppingBasketUpdateMethods,
      orderId,
      isEmployeeOrder,
    ]
  );

  return (
    <CheckoutContextCoCo.Provider value={checkoutContextValue}>
      <FormProvider {...methods}>
        {errorNotification && (
          <div className="of-quote-top-notification">
            <CL.Notification
              type="error"
              text={errorNotification}
              onNotificationHide={() => setErrorNotification(undefined)}
            />
          </div>
        )}
        <form onSubmit={() => {}} className="of-product-order">
          <CL.Stepper
            orientation="horizontal"
            sticky={true}
            activeStep={activeStep}
            steps={[
              {
                id: PlaceOrderStep.CUSTOMER,
                name: t.IY5V(customerMsg),
                content: (
                  <CustomerStepCoCo
                    companyName={companyName}
                    contactInfo={contactInfo}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                  />
                ),
                icon: <CL.Icon icon="office" />,
                onClick: () => moveToStep(PlaceOrderStep.CUSTOMER),
                completed: stepState.completed.has(PlaceOrderStep.CUSTOMER),
                disabled: isStepDisabled(PlaceOrderStep.CUSTOMER),
              },
              {
                id: PlaceOrderStep.PRODUCTS,
                name: t.IY4Z(productsMsg),
                content: (
                  <ProductsStepCoCo
                    validationRef={productsStepRef}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                    contactsUpdateMethods={contactsUpdateMethods}
                    customerInfo={customerInfo}
                    installationDateSelection={{
                      availableInstallationDates,
                      installationAvailability,
                      availabilityCheckedDate,
                      onCheckAvailability: () => handleStepAction(checkInstallationAvailability),
                      onConfirmInstallationTimeSlots: timeSlotIds =>
                        handleStepAction(() => confirmInstallationTimeSlots(timeSlotIds)),
                    }}
                    orderSummary={<PlaceOrderSummaryCoCo {...summaryProps} defaultOpen={false} />}
                  />
                ),
                icon: <CL.Icon icon="group" />,
                onClick: () => moveToStep(PlaceOrderStep.PRODUCTS),
                completed: stepState.completed.has(PlaceOrderStep.PRODUCTS),
                disabled: isStepDisabled(PlaceOrderStep.PRODUCTS),
              },
              {
                id: PlaceOrderStep.DELIVERY,
                name: t.BSE3('Delivery'),
                content: (
                  <DeliveryStepCoCo
                    validationRef={deliveryStepRef}
                    {...props}
                    onNext={handleNextStep}
                    onPrevious={handlePreviousStep}
                    deliveryMethodGroups={deliveryMethodGroups}
                    deliveryOptions={deliveryOptions}
                    customerPrimaryAddress={customerPrimaryAddress}
                    orderSummary={<PlaceOrderSummaryCoCo {...summaryProps} defaultOpen={false} />}
                  />
                ),
                icon: <CL.Icon icon="delivery" />,
                onClick: () => moveToStep(PlaceOrderStep.DELIVERY),
                completed: stepState.completed.has(PlaceOrderStep.DELIVERY),
                disabled: isStepDisabled(PlaceOrderStep.DELIVERY),
              },
              {
                id: PlaceOrderStep.PAYMENT,
                name: t.D9Q4('Payment'),
                content: (
                  <PaymentStepCoCo
                    contactInfo={contactInfo}
                    isCardPaymentPossible={
                      !isEmployeeOrder && isCardPaymentPossible(shoppingBasket, selectedProductOffers)
                    }
                    isDeviceItemsOnly={isDeviceItemsOnly(shoppingBasket)}
                    onCreatingNewBillingAccountChange={setIsCreatingNewBillingAccount}
                    orderSummary={<PlaceOrderSummaryCoCo {...summaryProps} defaultOpen={false} />}
                    isSubmittingOrder={isSubmittingOrder}
                    onSubmit={validateBeforeSubmit}
                    onPrevious={handlePreviousStep}
                    onBillingAccountError={setErrorNotification}
                    mdmId={mdmId}
                    billingAccountCreation={{
                      billingAccountType: BillingAccountType.SFDC,
                      billChannels: billChannels ?? [],
                      customer: customerInfo,
                      relatedParties: (customerInfo.relatedParty ?? []).map(party => ({
                        id: party.id,
                        role: party.role,
                        name: party.name,
                        referredType: party.referredType,
                      })),
                    }}
                  />
                ),
                icon: <CL.Icon icon="money" />,
                onClick: () => moveToStep(PlaceOrderStep.PAYMENT),
                completed: stepState.completed.has(PlaceOrderStep.PAYMENT),
                disabled: isStepDisabled(PlaceOrderStep.PAYMENT),
              },
            ].filter(step => stepOrder.includes(step.id))}
          />
          {pendingStep !== undefined && (
            <ConfirmModal onStay={handleCancelNavigation} onContinue={handleConfirmNavigation} />
          )}
        </form>
        {(isStepLoading || isSubmittingOrder) && <LoadingOverlayCoCo label={t.VZZW(processingMsg)} />}
      </FormProvider>
    </CheckoutContextCoCo.Provider>
  );
};
